/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { Card, CardHeader, CardBody, Text, Flex } from '@backstage/ui'
import { useEntity } from '@backstage/plugin-catalog-react'
import { Switch, Tooltip } from '@material-ui/core'
import { useState } from 'react'
import { ComponentsGrid } from './ComponentsGrid'

/** @public */
export const OwnershipCard = (props: {
  variant?: 'fullHeight'
  entityFilterKind?: string[]
  hideRelationsToggle?: boolean
  relationsType?: string
  entityLimit?: number
  groupBy?: 'kind' | 'type'
}) => {
  const {
    entityFilterKind,
    hideRelationsToggle,
    relationsType,
    entityLimit = 6,
    groupBy
  } = props
  const relationsToggle = hideRelationsToggle === undefined ? false : hideRelationsToggle
  const { entity } = useEntity()
  const isGroup = entity.kind === 'Group'
  const [getRelationsType, setRelationsType] = useState(relationsType || 'direct')

  return (
    <Card style={{ height: '100%' }}>
      <CardHeader>
        <Text variant="title-x-small" weight="bold">
          Ownership
        </Text>
      </CardHeader>
      <CardBody>
        {!relationsToggle && (
          <Flex justify="end" align="center" pb="2">
            <Text variant="body-medium" style={{ whiteSpace: 'nowrap' }}>
              Direct Relations
            </Text>
            <Tooltip
              placement="top"
              arrow
              title={`${
                getRelationsType === 'direct' ? 'Direct' : 'Aggregated'
              } Relations`}
            >
              <Switch
                color="primary"
                checked={getRelationsType !== 'direct'}
                onChange={() =>
                  getRelationsType === 'direct'
                    ? setRelationsType('aggregated')
                    : setRelationsType('direct')
                }
                name="pin"
                inputProps={{ 'aria-label': 'Ownership Type Switch' }}
                disabled={!isGroup}
              />
            </Tooltip>
            <Text variant="body-medium" style={{ whiteSpace: 'nowrap' }}>
              Aggregated Relations
            </Text>
          </Flex>
        )}
        <ComponentsGrid
          entity={entity}
          entityLimit={entityLimit}
          relationsType={getRelationsType}
          isGroup={isGroup}
          entityFilterKind={entityFilterKind}
          groupBy={groupBy}
        />
      </CardBody>
    </Card>
  )
}
